import { useEffect, useRef, useCallback } from 'react';
import { supabase } from '../../lib/supabase';

export interface UseTableSubscriptionsProps {
  tableId: string;
  onRowInsert?: () => void;
  onCellUpdate?: (columnName: string, rowId: string) => void;
  onDataRefresh?: () => void;
}

/**
 * Hook for managing real-time Supabase subscriptions for table updates
 */
export const useTableSubscriptions = ({
  tableId,
  onRowInsert,
  onCellUpdate,
  onDataRefresh,
}: UseTableSubscriptionsProps): void => {
  const isMountedRef = useRef(true);

  // Track mounted state
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  /**
   * Handle cell changes (INSERT/UPDATE)
   */
  const handleCellChange = useCallback(async (payload: any, eventType: 'INSERT' | 'UPDATE') => {
    if (!isMountedRef.current) return;

    if (payload.new && payload.new.id) {
      try {
        // Fetch the cell details to identify which specific cell was changed
        const { data: cellData, error } = await supabase
          .from('lead_cells')
          .select(`
            id,
            row_id,
            lead_columns!inner(name),
            lead_rows!inner(lead_table_id)
          `)
          .eq('id', payload.new.id)
          .eq('lead_rows.lead_table_id', tableId)
          .single();

        if (!error && cellData) {
          const columns: any = cellData.lead_columns;
          const columnName = Array.isArray(columns) ? columns[0]?.name : columns?.name;
          const rowId = cellData.row_id;

          // Notify about the specific cell update
          onCellUpdate?.(columnName, rowId);
        }
      } catch (error) {
        console.error(`Error processing cell ${eventType.toLowerCase()}:`, error);
      }
    }

    // Refresh table data
    onDataRefresh?.();
  }, [tableId, onCellUpdate, onDataRefresh]);

  // Set up real-time subscription for new rows
  useEffect(() => {
    if (!tableId) return;

    const channel = supabase
      .channel(`lead_rows:${tableId}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'lead_rows',
          filter: `lead_table_id=eq.${tableId}`
        },
        () => {
          if (!isMountedRef.current) return;
          onRowInsert?.();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [tableId, onRowInsert]);

  // Set up real-time subscription for cell updates (enrichment results)
  useEffect(() => {
    if (!tableId) return;

    const cellsChannel = supabase
      .channel(`lead_cells_${tableId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'lead_cells',
        },
        async (payload) => {
          if (!isMountedRef.current) return;
          await handleCellChange(payload, 'UPDATE');
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'lead_cells',
        },
        async (payload) => {
          if (!isMountedRef.current) return;
          await handleCellChange(payload, 'INSERT');
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(cellsChannel);
    };
  }, [tableId, handleCellChange]);
};
